import { useState, useRef, useEffect } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { UploadCloud, Loader2, X } from "lucide-react";
import StateSelector from "@/components/StateSelector";
import UserSelector from "@/components/UserSelector";
import ModalShell from "@/components/ModalShell";

const CATEGORIES = ["unassigned", "product", "strain", "marketing", "retail", "sales", "merch"];

function autoTitle(filename, opts = {}) {
  if (!filename) return "";
  const base = filename
    .replace(/\.[^.]+$/, "")
    .replace(/[-_]+/g, " ")
    .replace(/\s+/g, " ")
    .trim();
  const cased = base
    .split(" ")
    .map((w) => (w.length > 2 ? w.charAt(0).toUpperCase() + w.slice(1) : w.toUpperCase()))
    .join(" ");
  const states = (opts.states || "").split(",").map((s) => s.trim()).filter(Boolean);
  const parts = [cased];
  if (states.length) parts.push(states.join("/"));
  else if (opts.category && opts.category !== "unassigned") {
    parts.push(opts.category.charAt(0).toUpperCase() + opts.category.slice(1));
  }
  return parts.join(" — ");
}

export default function UploadAssetModal({ open, onClose, defaultCategory = "marketing", defaultFolderId = null, onUploaded }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [drag, setDrag] = useState(false);
  const [busy, setBusy] = useState(false);
  // Whether the current title is still auto-derived. Flips to false once the
  // user edits it manually so we don't clobber their input.
  const [titleAuto, setTitleAuto] = useState(true);
  const initialCategory = defaultCategory || "unassigned";
  const [form, setForm] = useState({
    title: "",
    description: "",
    category: initialCategory,
    subcategory: "",
    tags: "",
    states: "",
    allowed_user_ids: "",
  });

  useEffect(() => {
    if (open) {
      setForm((f) => ({ ...f, category: defaultCategory || "unassigned" }));
    } else {
      setFile(null);
      setTitleAuto(true);
      setForm({ title: "", description: "", category: defaultCategory || "unassigned", subcategory: "", tags: "", states: "", allowed_user_ids: "" });
      if (fileRef.current) fileRef.current.value = "";
    }
  }, [open, defaultCategory]);

  // Auto-fill / refresh title whenever file, category, or states change (unless user edited manually).
  useEffect(() => {
    if (!file) return;
    if (!titleAuto) return;
    const t = autoTitle(file.name, { category: form.category, states: form.states });
    setForm((f) => ({ ...f, title: t }));
  }, [file, form.category, form.states, titleAuto]);

  if (!open) return null;

  async function submit(e) {
    e?.preventDefault();
    if (!file) return toast.error("Select a file first");
    if (!form.title.trim()) return toast.error("Title is required");
    setBusy(true);
    const fd = new FormData();
    fd.append("file", file);
    Object.entries(form).forEach(([k, v]) => fd.append(k, v));
    if (defaultFolderId) fd.append("folder_id", defaultFolderId);
    try {
      const { data } = await api.post("/admin/assets/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success(`${data.title} uploaded`);
      onUploaded && onUploaded(data);
      onClose();
    } catch (err) {
      toast.error(formatApiError(err));
    }
    setBusy(false);
  }

  return (
    <ModalShell open={open} onClose={onClose} maxWidth="max-w-xl" testId="upload-modal">
      <button onClick={onClose} className="absolute top-4 right-4 p-1 hover:bg-surface-elev rounded-sm" data-testid="upload-close" aria-label="Close">
        <X className="h-4 w-4" />
      </button>
      <div className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-2">Upload</div>
      <h2 className="font-display text-3xl uppercase font-bold tracking-tight">New Asset</h2>

      <form onSubmit={submit} className="mt-6 space-y-5">
          <div
            onDragOver={(e) => { e.preventDefault(); setDrag(true); }}
            onDragLeave={() => setDrag(false)}
            onDrop={(e) => { e.preventDefault(); setDrag(false); const f = e.dataTransfer.files?.[0]; if (f) setFile(f); }}
            className={`border-2 border-dashed rounded-sm p-8 text-center transition ${drag ? "border-foreground bg-surface-elev" : "border-border"}`}
          >
            <UploadCloud className="h-7 w-7 mx-auto text-muted-foreground" />
            <div className="mt-3 text-sm">{file ? file.name : "Drag & drop or click to select"}</div>
            <input ref={fileRef} type="file" className="hidden" onChange={(e) => setFile(e.target.files?.[0] || null)} data-testid="upload-file" />
            <button type="button" onClick={() => fileRef.current?.click()} className="mt-3 border border-border px-4 py-1.5 text-xs uppercase tracking-widest font-mono hover:bg-surface-elev transition" data-testid="upload-pick">
              Select File
            </button>
          </div>

          <Field label="Title">
            <input required value={form.title} onChange={(e) => { setForm({ ...form, title: e.target.value }); setTitleAuto(false); }} data-testid="upload-title" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
          </Field>

          <div className="grid grid-cols-2 gap-4">
            <Field label="Category">
              <select value={form.category} onChange={(e) => setForm({ ...form, category: e.target.value })} data-testid="upload-cat" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground capitalize">
                {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
              </select>
            </Field>
            <Field label="Subcategory (optional)">
              <input value={form.subcategory} onChange={(e) => setForm({ ...form, subcategory: e.target.value })} data-testid="upload-sub" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
            </Field>
          </div>

          <Field label="Description">
            <textarea rows={6} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} data-testid="upload-desc" placeholder="Full description — line breaks and paragraphs will be preserved." className="w-full bg-transparent border border-border rounded-sm p-3 text-sm outline-none focus:border-foreground whitespace-pre-wrap" />
          </Field>

          <div className="grid grid-cols-1 gap-4">
            <Field label="Keywords (comma-separated, for search)">
              <input value={form.tags} onChange={(e) => setForm({ ...form, tags: e.target.value })} data-testid="upload-tags" placeholder="e.g. social, instagram, summer" className="w-full bg-transparent border-b border-border py-2 text-sm outline-none focus:border-foreground" />
            </Field>
          </div>

          <StateSelector
            value={form.states}
            onChange={(str) => setForm({ ...form, states: str })}
            testIdPrefix="upload-state"
          />

          <UserSelector
            value={form.allowed_user_ids}
            onChange={(str) => setForm({ ...form, allowed_user_ids: str })}
            testIdPrefix="upload-users"
          />

          <button type="submit" disabled={busy} className="w-full bg-foreground text-background py-3 uppercase tracking-widest text-xs font-semibold flex items-center justify-center gap-2 disabled:opacity-50" data-testid="upload-submit">
            {busy && <Loader2 className="h-4 w-4 animate-spin" />}
            Upload Asset
          </button>
      </form>
    </ModalShell>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="font-mono text-[10px] uppercase tracking-[0.3em] text-muted-foreground mb-1 block">{label}</label>
      {children}
    </div>
  );
}
